/**
 * DataFreshness - Sistema de Validade e Atualização de Dados
 * 
 * PRINCÍPIO: O usuário SEMPRE sabe se o dado está atualizado ou não
 * 
 * Baseado no Tripé:
 * ⚖️ JUSTIÇA - Transparência sobre a validade dos dados
 * 🔒 SEGURANÇA - Usuário toma decisões com informação correta
 * ✨ SIMPLICIDADE - Indicadores visuais claros
 */

// Configuração de validade por tipo de dado
export const DATA_FRESHNESS_CONFIG = {
  // Dados que mudam DIARIAMENTE
  cambio: {
    name: 'Dólar (Câmbio)',
    maxAgeDays: 5, // Considera fins de semana e feriados
    updateFrequency: 'Diária (dias úteis)',
    validityPeriod: 'Dias úteis',
    source: 'BCB/PTAX',
    canAutoUpdate: true,
    apiUrl: 'https://economia.awesomeapi.com.br/json/last/USD-BRL',
    note: 'Não atualiza fins de semana e feriados',
  },
  internacional: {
    name: 'SMR20 Internacional',
    maxAgeDays: 10, // ~7 dias úteis considerando fins de semana e feriados
    updateFrequency: 'Diária (dias úteis)',
    validityPeriod: 'Dias úteis de pregão',
    source: 'Bolsa Singapura/Trading Economics',
    canAutoUpdate: true,
    apiUrl: null,
    note: 'Bolsa fecha em fins de semana e feriados',
  },
  
  // Dados que mudam MENSALMENTE
  coagulo: {
    name: 'Coágulo DRC 53%',
    maxAgeDays: 35, // Mensal com margem
    updateFrequency: 'Mensal',
    validityPeriod: 'Mês de referência',
    source: 'IEA/Notícias Agrícolas',
    canAutoUpdate: false,
    checkUrl: 'https://www.noticiasagricolas.com.br/cotacoes/latex',
  },
  preco_importacao: {
    name: 'Preço Importação IEA',
    maxAgeDays: 35,
    updateFrequency: 'Mensal',
    validityPeriod: 'Mês de referência',
    source: 'IEA/CNA',
    canAutoUpdate: false,
    checkUrl: 'http://ciagri.iea.sp.gov.br/bancodedados/borracha',
  },
  
  // Dados que mudam BIMESTRALMENTE
  geb10_apabor: {
    name: 'GEB-10 APABOR',
    maxAgeDays: 65,
    updateFrequency: 'Bimestral',
    validityPeriod: 'Bimestre de referência',
    source: 'APABOR',
    canAutoUpdate: false,
    checkUrl: 'https://www.noroesteborracha.com.br/preco',
    bimestres: ['Dez-Jan', 'Fev-Mar', 'Abr-Mai', 'Jun-Jul', 'Ago-Set', 'Out-Nov'],
  },
  geb10_mercado: {
    name: 'GEB-10 Mercado SP',
    maxAgeDays: 35, // Mensal
    updateFrequency: 'Mensal',
    validityPeriod: 'Mês de referência',
    source: 'Borracha Natural (Lateks)',
    canAutoUpdate: false,
    checkUrl: 'https://borrachanatural.agr.br',
  },
  
  // Dados que mudam TRIMESTRALMENTE
  custo_producao_cot: {
    name: 'Custo Produção (COT)',
    maxAgeDays: 120, // Trimestral/Anual
    updateFrequency: 'Trimestral',
    validityPeriod: 'Safra de referência',
    source: 'CONAB',
    canAutoUpdate: false,
    checkUrl: 'https://www.conab.gov.br/info-agro/custos-de-producao',
  },
};

/**
 * Calcula o status de frescor de um dado
 * @param {string} referenceDate - Data de referência do dado (ex: "Dez/2025", "18/12/2025", "Dez-Jan/2025-26")
 * @param {number} maxAgeDays - Idade máxima permitida em dias
 * @returns {object} - { status: 'fresh'|'warning'|'stale', daysOld, message }
 */
export const calculateFreshness = (referenceDate, maxAgeDays) => {
  if (!referenceDate) {
    return { status: 'unknown', daysOld: null, message: 'Data não informada' };
  }

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  let refDate;

  // Verificar se é formato bimestral (ex: "Dez-Jan/2025-26" ou "Out-Nov/2025")
  if (referenceDate.includes('-') && referenceDate.includes('/')) {
    const monthMap = {
      'Jan': 0, 'Fev': 1, 'Mar': 2, 'Abr': 3, 'Mai': 4, 'Jun': 5,
      'Jul': 6, 'Ago': 7, 'Set': 8, 'Out': 9, 'Nov': 10, 'Dez': 11
    };
    
    // Extrair os meses do bimestre
    const parts = referenceDate.split('/')[0].split('-');
    const firstMonth = monthMap[parts[0]];
    const secondMonth = monthMap[parts[1]];
    
    // Verificar se estamos dentro do bimestre
    // Ex: Dez-Jan = meses 11 e 0 (dezembro e janeiro)
    if (firstMonth === 11 && secondMonth === 0) {
      // Bimestre Dez-Jan cruza o ano
      if (currentMonth === 11 || currentMonth === 0) {
        return { status: 'fresh', daysOld: 0, message: 'Período vigente' };
      }
    } else if (currentMonth >= firstMonth && currentMonth <= secondMonth) {
      // Estamos dentro do bimestre
      return { status: 'fresh', daysOld: 0, message: 'Período vigente' };
    }
    
    // Se não estamos no bimestre, calcular quantos dias desde o fim do bimestre
    let endOfBimestre;
    if (firstMonth === 11) {
      // Dez-Jan termina em 31 de janeiro
      endOfBimestre = new Date(currentYear, 1, 31);
    } else {
      endOfBimestre = new Date(currentYear, secondMonth + 1, 0); // Último dia do segundo mês
    }
    
    const diffTime = now - endOfBimestre;
    const daysOld = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (daysOld < 0) {
      return { status: 'fresh', daysOld: 0, message: 'Período vigente' };
    }
    
    if (daysOld <= 15) {
      return { status: 'warning', daysOld, message: 'Verificar se há novo período disponível' };
    }
    
    return { status: 'stale', daysOld, message: 'Referente a período anterior' };
  }

  // Tentar parsear diferentes formatos de data
  if (referenceDate.includes('/202')) {
    // Formato: "Dez/2025" ou "18/12/2025"
    if (referenceDate.length <= 10 && referenceDate.split('/').length === 2) {
      // Formato: "Dez/2025"
      const monthMap = {
        'Jan': 0, 'Fev': 1, 'Mar': 2, 'Abr': 3, 'Mai': 4, 'Jun': 5,
        'Jul': 6, 'Ago': 7, 'Set': 8, 'Out': 9, 'Nov': 10, 'Dez': 11
      };
      const parts = referenceDate.split('/');
      const month = monthMap[parts[0]] ?? parseInt(parts[0]) - 1;
      const year = parseInt(parts[1]);
      
      // Para dados mensais, verificar se estamos no mesmo mês
      if (month === currentMonth && year === currentYear) {
        return { status: 'fresh', daysOld: 0, message: 'Período vigente' };
      }
      
      refDate = new Date(year, month, 15); // Meio do mês
    } else {
      // Formato: "18/12/2025"
      const parts = referenceDate.split('/');
      refDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    }
  } else {
    return { status: 'unknown', daysOld: null, message: 'Formato de data não reconhecido' };
  }

  const diffTime = now - refDate;
  const daysOld = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (daysOld < 0) {
    return { status: 'fresh', daysOld: 0, message: 'Período vigente' };
  }

  if (daysOld <= maxAgeDays) {
    return { status: 'fresh', daysOld, message: 'Período vigente' };
  }

  if (daysOld <= maxAgeDays * 1.5) {
    return { status: 'warning', daysOld, message: 'Verificar se há novo período disponível' };
  }

  return { status: 'stale', daysOld, message: 'Referente a período anterior' };
};

/**
 * Retorna o indicador visual de frescor
 * LINGUAGEM NEUTRA: Não diz que dado é "inválido", apenas indica período de validade
 */
export const getFreshnessIndicator = (status) => {
  switch (status) {
    case 'fresh':
      return { emoji: '🟢', color: 'text-green-600', bgColor: 'bg-green-100', label: 'Vigente' };
    case 'warning':
      return { emoji: '🟡', color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Verificar novo período' };
    case 'stale':
      return { emoji: '🟠', color: 'text-orange-600', bgColor: 'bg-orange-100', label: 'Período anterior' };
    default:
      return { emoji: '⚪', color: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Sem data' };
  }
};

/**
 * Busca câmbio atualizado da API do BCB (gratuita)
 */
export const fetchCambioAutomatico = async () => {
  try {
    const response = await fetch('https://economia.awesomeapi.com.br/json/last/USD-BRL');
    if (response.ok) {
      const data = await response.json();
      const usd = data.USDBRL;
      return {
        value: parseFloat(usd.bid),
        date: new Date().toLocaleDateString('pt-BR'),
        source: 'AwesomeAPI/BCB',
      };
    }
  } catch (error) {
    console.error('Erro ao buscar câmbio:', error);
  }
  return null;
};

/**
 * Calcula próximas datas de atualização baseado no calendário real das fontes
 */
export const getNextUpdateDates = () => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const updates = [];

  // DÓLAR - Diário (dias úteis)
  const nextBusinessDay = new Date(now);
  nextBusinessDay.setDate(now.getDate() + 1);
  while (nextBusinessDay.getDay() === 0 || nextBusinessDay.getDay() === 6) {
    nextBusinessDay.setDate(nextBusinessDay.getDate() + 1);
  }
  updates.push({
    type: 'cambio',
    name: 'Dólar (Câmbio)',
    frequency: 'Diária',
    date: nextBusinessDay,
    dateLabel: nextBusinessDay.toLocaleDateString('pt-BR'),
    source: 'BCB/PTAX',
    note: 'Atualização automática disponível',
    canAutoUpdate: true,
  });

  // PREÇO IMPORTAÇÃO IEA - Mensal (geralmente dia 15-20 do mês)
  let nextIEADate = new Date(currentYear, currentMonth, 15);
  if (now.getDate() > 20) {
    nextIEADate = new Date(currentYear, currentMonth + 1, 15);
  }
  updates.push({
    type: 'preco_importacao',
    name: 'Preço Importação IEA',
    frequency: 'Mensal',
    date: nextIEADate,
    dateLabel: `~15-20 de ${nextIEADate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`,
    source: 'IEA/CNA',
    note: 'Publicado mensalmente pelo IEA',
    checkUrl: 'http://ciagri.iea.sp.gov.br/bancodedados/borracha',
  });

  // COÁGULO - Mensal (junto com IEA ou Notícias Agrícolas)
  updates.push({
    type: 'coagulo',
    name: 'Coágulo DRC 53%',
    frequency: 'Mensal',
    date: nextIEADate,
    dateLabel: `~15-20 de ${nextIEADate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`,
    source: 'IEA/Notícias Agrícolas',
    note: 'Acompanha publicação do IEA',
    checkUrl: 'https://www.noticiasagricolas.com.br/cotacoes/latex',
  });

  // GEB-10 APABOR - Bimestral (publicado no início do primeiro mês do bimestre)
  const bimestres = [
    { months: [11, 0], label: 'Dez-Jan', startMonth: 11, publishMonth: 'Dezembro' },  // Dez-Jan publica em Dezembro
    { months: [1, 2], label: 'Fev-Mar', startMonth: 1, publishMonth: 'Fevereiro' },   // Fev-Mar publica em Fevereiro
    { months: [3, 4], label: 'Abr-Mai', startMonth: 3, publishMonth: 'Abril' },       // Abr-Mai publica em Abril
    { months: [5, 6], label: 'Jun-Jul', startMonth: 5, publishMonth: 'Junho' },       // Jun-Jul publica em Junho
    { months: [7, 8], label: 'Ago-Set', startMonth: 7, publishMonth: 'Agosto' },      // Ago-Set publica em Agosto
    { months: [9, 10], label: 'Out-Nov', startMonth: 9, publishMonth: 'Outubro' },    // Out-Nov publica em Outubro
  ];
  
  // Encontrar próximo bimestre (o que ainda não começou)
  let nextBimestre = null;
  let currentBimestre = null;
  
  for (const bim of bimestres) {
    const startDate = new Date(currentYear, bim.startMonth, 1);
    // Ajustar para Dez-Jan que cruza o ano
    if (bim.startMonth === 11) {
      // Dezembro do ano atual
      if (currentMonth === 11) {
        currentBimestre = { ...bim, year: currentYear };
      } else if (currentMonth === 0) {
        currentBimestre = { ...bim, year: currentYear - 1 };
      }
    } else if (currentMonth >= bim.months[0] && currentMonth <= bim.months[1]) {
      currentBimestre = { ...bim, year: currentYear };
    }
    
    if (startDate > now && !nextBimestre) {
      nextBimestre = { ...bim, year: currentYear };
    }
  }
  
  // Se estamos em Dez-Jan, o próximo é Fev-Mar
  if (currentMonth === 11 || currentMonth === 0) {
    nextBimestre = { ...bimestres[1], year: currentMonth === 0 ? currentYear : currentYear + 1 };
    currentBimestre = { ...bimestres[0], year: currentMonth === 0 ? currentYear - 1 : currentYear };
  }
  
  if (!nextBimestre) {
    // Próximo ano - Dez-Jan
    nextBimestre = { ...bimestres[0], year: currentYear + 1 };
  }
  
  updates.push({
    type: 'geb10_apabor',
    name: 'GEB-10 APABOR',
    frequency: 'Bimestral',
    date: new Date(nextBimestre.year, nextBimestre.startMonth, 1),
    dateLabel: `Início de ${nextBimestre.publishMonth}/${nextBimestre.year}`,
    currentPeriod: currentBimestre ? `${currentBimestre.label}/${currentBimestre.year}` : null,
    source: 'APABOR',
    note: `Bimestre atual: ${currentBimestre?.label || 'N/A'} • Próximo: ${nextBimestre.label}`,
    checkUrl: 'https://www.noroesteborracha.com.br/preco',
  });

  // Ordenar por data
  return updates.sort((a, b) => a.date - b.date);
};

/**
 * Retorna calendário completo de atualizações do ano
 */
export const getUpdateCalendar = () => {
  const calendar = {
    diaria: ['Dólar (BCB/PTAX)', 'SMR20 Internacional'],
    mensal: {
      dados: ['Preço Importação IEA', 'Coágulo DRC 53%', 'GEB-10 Mercado SP'],
      periodoPublicacao: 'Entre os dias 15-20 de cada mês',
    },
    bimestral: {
      dados: ['GEB-10 APABOR'],
      periodos: [
        { label: 'Dez-Jan', publicacao: 'Início de Janeiro' },
        { label: 'Fev-Mar', publicacao: 'Início de Março' },
        { label: 'Abr-Mai', publicacao: 'Início de Maio' },
        { label: 'Jun-Jul', publicacao: 'Início de Julho' },
        { label: 'Ago-Set', publicacao: 'Início de Setembro' },
        { label: 'Out-Nov', publicacao: 'Início de Novembro' },
      ],
    },
    trimestral: {
      dados: ['Custo de Produção CONAB'],
      periodoPublicacao: 'Atualizado por safra',
    },
  };
  return calendar;
};

export default {
  DATA_FRESHNESS_CONFIG,
  calculateFreshness,
  getFreshnessIndicator,
  fetchCambioAutomatico,
  getNextUpdateDates,
};
